import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useTriggerTowerServiceAction } from '@/queries/deviceQueries'
import { showToast } from '@/theme/notifications'
import { TowerAction } from '@/types/device'

type TowerActionConfirmModalProps = {
  opened: boolean
  action: TowerAction
  deviceId: string
  latestVersion?: string
  onClose: () => void
  onSuccess?: (action: TowerAction) => void
}

type ActionConfig = {
  buttonId: string
  confirmationId: string
  buttonColor?: string
}

const ACTION_CONFIG: Record<TowerAction, ActionConfig> = {
  deploy: {
    buttonId: 'tower.hardware.deploy',
    confirmationId: 'tower.hardware.deploy.confirmation'
  },
  update: {
    buttonId: 'tower.hardware.update',
    confirmationId: 'tower.hardware.update.confirmation'
  },
  start: {
    buttonId: 'tower.hardware.start',
    confirmationId: 'tower.hardware.start.confirmation'
  },
  stop: {
    buttonId: 'tower.hardware.stop',
    confirmationId: 'tower.hardware.stop.confirmation'
  },
  restart: {
    buttonId: 'tower.hardware.restart',
    confirmationId: 'tower.hardware.restart.confirmation'
  },
  uninstall: {
    buttonId: 'tower.hardware.uninstall',
    confirmationId: 'tower.hardware.uninstall.confirmation',
    buttonColor: 'red'
  }
}

export const TowerActionConfirmModal = ({
  opened,
  action,
  deviceId,
  latestVersion,
  onClose,
  onSuccess
}: TowerActionConfirmModalProps) => {
  const intl = useIntl()
  const config = ACTION_CONFIG[action]

  const { mutateAsync: triggerAction, isPending } =
    useTriggerTowerServiceAction()

  const handleConfirm = async () => {
    try {
      await triggerAction({
        deviceId,
        action,
        version:
          action === 'deploy' || action === 'update' ? latestVersion : undefined
      })
      onSuccess?.(action)
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.hardware.action.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="tower.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm">
          <FormattedMessage
            id={config.confirmationId}
            values={{ version: latestVersion }}
          />
        </Text>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button
            miw={120}
            color={config.buttonColor}
            onClick={() => void handleConfirm()}
          >
            <FormattedMessage id={config.buttonId} />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
